import { useState, useEffect } from "react";
import { Search, ChevronUp, AlertTriangle, Info, Utensils, Download, Printer, Clock, Users, HelpCircle, Bus, Save } from "lucide-react";
import SopSwitcher from "./SopSwitcher";
import SopImage from "./SopImage";
import "../styles/tutorialPrint.css";

function Why({ children }) {
  return (
    <div className="sop-why sop-block bg-amber-50 border-l-4 border-amber-600 p-4 rounded-r-lg my-4">
      <p className="text-sm font-bold text-amber-900 mb-1 flex items-center gap-1"><Info className="w-4 h-4" /> Why does this matter?</p>
      <p className="text-sm text-amber-800">{children}</p>
    </div>
  );
}

function Mistakes({ items }) {
  return (
    <div className="sop-mistakes sop-block bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg my-4">
      <p className="text-sm font-bold text-red-900 mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> DO NOT do these things</p>
      <ul className="list-disc pl-5 space-y-1">
        {items.map((item, i) => <li key={i} className="text-sm text-red-800">{item}</li>)}
      </ul>
    </div>
  );
}

function Steps({ items }) {
  return (
    <ol className="sop-steps sop-block list-decimal pl-6 space-y-2 my-3">
      {items.map((item, i) => <li key={i} className="text-base text-gray-700 leading-relaxed">{item}</li>)}
    </ol>
  );
}

function Rule({ children }) {
  return (
    <div className="sop-block p-4 bg-red-50 border-2 border-red-500 rounded-xl my-4">
      <p className="font-bold text-red-900">{children}</p>
    </div>
  );
}

const SECTIONS = [
  { id: "what-is-dining", num: "1", title: "What Is the Lunch Ordering App?" },
  { id: "how-to-login", num: "2", title: "How to Log In" },
  { id: "what-you-see", num: "3", title: "What You See After Login" },
  { id: "ordering-lunch", num: "4", title: "How to Order Lunch for Your Campers" },
  { id: "save-all", num: "5", title: "Save All Orders vs Save One" },
  { id: "deadline", num: "6", title: "The 9:30 AM Deadline" },
  { id: "allergies", num: "7", title: "Allergies — What the Labels Mean" },
  { id: "trip-lunches", num: "8", title: "Trip Lunch Orders" },
  { id: "troubleshooting", num: "9", title: "Something Not Working?" },
];

export default function TutorialDining() {
  const [search, setSearch] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const h = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const filtered = search ? SECTIONS.filter(s => s.title.toLowerCase().includes(search.toLowerCase())) : SECTIONS;

  return (
    <div className="min-h-screen bg-white" data-testid="tutorial-dining-page">
      <header className="sticky top-0 z-50 bg-amber-700 text-white shadow-lg print:static print:bg-white print:text-black">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-lg sm:text-xl font-bold">Rolling River Day Camp</h1>
              <p className="text-xs text-amber-200">Lunch App Tutorial</p>
            </div>
            <div className="hidden sm:block">
              <SopSwitcher current="/tutorialdining" accent="amber" />
            </div>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <div className="sm:hidden">
              <SopSwitcher current="/tutorialdining" accent="amber" />
            </div>
            <button onClick={() => window.print()} className="text-xs bg-amber-600 hover:bg-amber-500 px-3 py-1.5 rounded-lg font-medium inline-flex items-center gap-1" data-testid="print-btn">
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 lg:flex lg:gap-8">
        <nav aria-label="TOC" className="lg:w-64 lg:flex-shrink-0 mb-6 lg:mb-0 print:hidden">
          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden w-full text-left bg-gray-50 border rounded-xl p-3 text-sm font-bold text-gray-700 flex items-center justify-between" data-testid="toc-toggle">
            Table of Contents <ChevronUp className={`w-4 h-4 transition-transform ${menuOpen ? "" : "rotate-180"}`} />
          </button>
          <div className={`lg:sticky lg:top-20 ${menuOpen ? "block" : "hidden lg:block"} mt-2 lg:mt-0`}>
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input type="text" placeholder="Search sections..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="toc-search" />
            </div>
            <ul className="space-y-0.5 max-h-[70vh] overflow-y-auto">
              {filtered.map(s => (
                <li key={s.id}><a href={`#${s.id}`} onClick={() => setMenuOpen(false)} className="block px-3 py-1.5 text-sm rounded-lg hover:bg-amber-50 text-gray-600">{s.num}. {s.title}</a></li>
              ))}
            </ul>
          </div>
        </nav>

        <main className="flex-1 min-w-0">
          <section className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Lunch Ordering App — How to Use It</h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">Every morning, you log in and select a lunch for each camper in your group. The kitchen prepares exactly what you order. If you don't order, the camper doesn't eat.</p>
            <div className="bg-red-50 border-2 border-red-400 rounded-xl p-5 mb-5">
              <h3 className="text-lg font-bold text-red-900 mb-2">The Rule</h3>
              <p className="text-sm text-red-800">Lunch orders close at <strong>9:30 AM</strong>. There is a countdown timer on the screen. After 9:30, you CANNOT place or change orders. The kitchen starts prep immediately.</p>
            </div>
            <p className="text-xs text-gray-400">Website: rrdcdining.com</p>
          </section>

          {/* 1 */}
          <div id="what-is-dining" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><Utensils className="w-6 h-6 text-amber-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">1. What Is the Lunch Ordering App?</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">The Lunch Ordering App (<strong>rrdcdining.com</strong>) is where counselors order lunch for their campers every day. The kitchen sees your orders in real time and prepares food based on what you submit.</p>
            <p className="text-base text-gray-700">There are three sections on the home page:</p>
            <div className="space-y-2">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg"><strong className="text-sm text-amber-900">Group Login</strong> — <span className="text-sm text-amber-700">This is YOUR section. Enter your group code or name to order daily lunches.</span></div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg"><strong className="text-sm text-blue-900">Trip Lunch Orders</strong> — <span className="text-sm text-blue-700">Order lunches in advance for trip days. Each trip's window opens 1 week before the trip and closes the Friday before at 3:00 PM. Camelback and Splish Splash close the Friday two weeks before so the kitchen has time to prep. The first day of camp the system opens both Week 1 and Week 2 trips at once.</span></div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg"><strong className="text-sm text-gray-900">Kitchen / Admin Access</strong> — <span className="text-sm text-gray-700">For the kitchen and camp admin. Not for counselors.</span></div>
            </div>
          </div>

          {/* 2 */}
          <div id="how-to-login" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><Users className="w-6 h-6 text-amber-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">2. How to Log In</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <SopImage filename="dining_login.png" caption="rrdcdining.com login — single field. Type your group code (e.g. B1) and tap Continue." />
            <Steps items={[
              <span key="1">Open your browser. Go to <strong>rrdcdining.com</strong>.</span>,
              <span key="2">Under "Group Login", type your group code or group name. Examples: <strong>B1</strong>, <strong>B02</strong>, <strong>G1</strong>, or your group name like <strong>Lobsters</strong>.</span>,
              <span key="3">Tap <strong>Continue</strong>.</span>,
              <span key="4">You see a welcome message (e.g. "Welcome, B01 Lobsters!"), a countdown timer, and your camper list with lunch dropdowns.</span>,
            ]} />
            <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
              <p className="font-bold text-sm text-amber-900">No separate password needed.</p>
              <p className="text-sm text-amber-700">The dining app only requires your group code or group name. No password. Just type it and hit Continue.</p>
            </div>
          </div>

          {/* 3 */}
          <div id="what-you-see" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><Utensils className="w-6 h-6 text-amber-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">3. What You See After Login</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">After logging in, the screen shows:</p>
            <SopImage filename="dining_dashboard.png" caption="B01 Lobsters dining dashboard — countdown to 9:30 AM, progress bar, camper list with lunch dropdowns. Camper names redacted." />
            <ul className="list-disc pl-6 space-y-2 text-base text-gray-700">
              <li><strong>Group name</strong> at the top (e.g. "B01 Lobsters — Lunch Orders")</li>
              <li><strong>Countdown timer</strong> — shows how much time is left until 9:30 AM. It says something like "Orders close in: 45m 23s (9:30 AM EST)"</li>
              <li><strong>Progress bar</strong> — shows "X of Y ordered" (e.g. "0 of 15 ordered — 0%")</li>
              <li><strong>Order Date picker</strong> — defaults to today. You can change it if needed.</li>
              <li><strong>CAMPERS section</strong> — every camper in your group, listed with:
                <ul className="list-disc pl-6 mt-1">
                  <li>Camper name</li>
                  <li>Allergies label (e.g. "Allergies: None" or "Allergies: Dairy, Nuts")</li>
                  <li>A <strong>"Select lunch..."</strong> dropdown</li>
                  <li>A <strong>save button</strong> (floppy disk icon) next to the dropdown</li>
                </ul>
              </li>
              <li><strong>Save All Orders</strong> button at the bottom</li>
            </ul>
          </div>

          {/* 4 */}
          <div id="ordering-lunch" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><Save className="w-6 h-6 text-amber-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">4. How to Order Lunch for Your Campers</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <Rule>Every camper needs a lunch selection saved. A selection that is chosen but NOT saved does NOT reach the kitchen. Your roster only shows kids scheduled to be at camp that day — it changes day to day.</Rule>
            <div className="p-4 rounded-xl border-2 border-amber-300 bg-amber-50" data-testid="dining-absent-note">
              <p className="text-sm font-bold text-amber-900">Heads up — Absent campers are hidden</p>
              <p className="text-sm text-amber-900 mt-1">If a camper has been marked <strong>absent</strong> for the day in the Attendance App (rrdcattendance.com), they will <strong>not</strong> appear on your lunch ordering daily roster. You don't need to order lunch for absent kids — the kitchen is already pulling from the live attendance list. If a camper is missing from the list and you know they're at camp, double-check that they aren't accidentally marked absent first.</p>
            </div>
            <Steps items={[
              <span key="1">For each camper, tap the <strong>"Select lunch..."</strong> dropdown next to their name.</span>,
              <span key="2">Pick their lunch from the list. Options change daily — they might include things like Pizza, Chicken Nuggets, Grilled Cheese, Salad Bar, etc.</span>,
              <span key="3">Tap the <strong>save button</strong> next to that camper, or — if you forget — use <strong>"Save All Orders"</strong> at the bottom of the roster to save everything at once.</span>,
              <span key="4">If the camper has an allergy, a warning popup appears asking you to confirm before the order saves. Read it and only confirm if the meal is actually safe for that kid.</span>,
              <span key="5">The progress bar updates. Keep going until it shows "X of X ordered — 100%".</span>,
              <span key="6">Once saved, each camper's lunch shows up on their camper card in your daily roster (Camper Snapshot) — handy for confirming at lunchtime.</span>,
            ]} />
            <Mistakes items={[
              "Selecting a lunch from the dropdown but NOT tapping Save. The kitchen only sees saved orders. An unsaved selection is invisible.",
              "Tapping confirm on the allergy popup without reading it.",
              "Ordering after 9:30 AM. The system locks.",
            ]} />
            <Why>The kitchen starts prep at 9:30 AM sharp. They cook exactly what's in the system. No saved order = no food for that kid. An order that ignores allergies = a trip to the hospital.</Why>
          </div>

          {/* 5 */}
          <div id="save-all" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><Save className="w-6 h-6 text-amber-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">5. Save All Orders vs Save One</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">You have two ways to save:</p>
            <div className="space-y-2">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="font-bold text-sm text-amber-900">Save One (floppy disk icon next to each camper)</p>
                <p className="text-sm text-amber-700 mt-1">Saves that one camper's lunch immediately. Use this if you're doing them one at a time as kids tell you what they want.</p>
              </div>
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="font-bold text-sm text-green-900">Save All Orders (big button at the bottom)</p>
                <p className="text-sm text-green-700 mt-1">Saves ALL selected lunches at once. Use this if you've gone through the whole list and selected everyone's lunch, then want to save them all in one tap.</p>
              </div>
            </div>
            <p className="text-base text-gray-700 mt-3 font-medium">Either way works. Just make sure you actually tap a save button. Selecting without saving = nothing reaches the kitchen.</p>
          </div>

          {/* 6 */}
          <div id="deadline" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><Clock className="w-6 h-6 text-red-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">6. The 9:30 AM Deadline</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <Rule>Lunch orders close at 9:30 AM EST. The countdown timer on your screen shows exactly how much time you have left.</Rule>
            <p className="text-base text-gray-700">After 9:30 AM:</p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
              <li>You cannot place new orders</li>
              <li>You cannot change existing orders</li>
              <li>The kitchen starts cooking based on what was saved</li>
              <li>If a camper has no saved order, they get no food prepared</li>
            </ul>
            <p className="text-base text-gray-700 mt-3">Once 9:30 hits, only <strong>Brian Stein</strong> can reopen the ordering window. If you genuinely need a late order, find a supervisor — they have a radio — and they'll call Brian to ask him to reopen it.</p>
          </div>

          {/* 7 */}
          <div id="allergies" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><AlertTriangle className="w-6 h-6 text-amber-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">7. Allergies — What the Labels Mean</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">Under each camper's name, you see an allergy label.</p>
            <div className="space-y-2">
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg"><strong className="text-sm text-green-900">NKA</strong> — <span className="text-sm text-green-700">"No Known Allergies." Order anything. Don't worry.</span></div>
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg"><strong className="text-sm text-green-900">Allergies: None</strong> — <span className="text-sm text-green-700">Same thing — no food allergies on file.</span></div>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg"><strong className="text-sm text-red-900">Allergies: Dairy, Tree Nuts</strong> — <span className="text-sm text-red-700">This camper CANNOT eat that allergen. Pick a safe meal.</span></div>
            </div>
            <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl">
              <p className="font-bold text-amber-900 mb-1">Allergy confirmation popup</p>
              <p className="text-sm text-amber-800">If you try to save a meal for a camper with an allergy, a warning popup appears asking if you're sure. Read it. Only confirm if the meal is actually safe for that kid. If you're unsure, ask the kitchen or your supervisor.</p>
            </div>
            <Rule>If a camper has a food allergy, DO NOT order them something that contains that allergen. The kitchen has allergy protocols, but you are the first line of defense.</Rule>
          </div>

          {/* 8 */}
          <div id="trip-lunches" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><Bus className="w-6 h-6 text-amber-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">8. Trip Lunch Orders</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <p className="text-base text-gray-700">When your group has a trip, lunches are ordered in advance through the <strong>"Trip Lunch Orders"</strong> button on the rrdcdining.com home page (no login needed — it's a separate button).</p>
            <Steps items={[
              <span key="1">From the home page, tap <strong>"Trip Lunch Orders"</strong>.</span>,
              <span key="2">You see upcoming trips grouped by date.</span>,
              <span key="3">Find your group under the trip date and tap it.</span>,
              <span key="4">Select a lunch for each camper. Save (each row has a save button, or use Save All at the bottom).</span>,
            ]} />

            <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl">
              <p className="font-bold text-amber-900 mb-1">Ordering window</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-amber-800">
                <li>Opens <strong>1 week before</strong> each trip.</li>
                <li>Closes the <strong>Friday before</strong> the trip at <strong>3:00 PM</strong>.</li>
                <li><strong>Camelback and Splish Splash</strong> close the <strong>Friday two weeks before</strong> the trip so the kitchen has the extra prep time those big-day trips need.</li>
                <li><strong>First week of camp:</strong> the system opens on Day 1 with both <strong>Week 1 AND Week 2</strong> trips visible at once. Order them all that week.</li>
              </ul>
              <p className="text-xs text-amber-700 mt-2">Locked = the window has closed. Contact your supervisor for changes.</p>
            </div>

            <p className="text-sm text-gray-700">Spreading the window like this also nudges you to actually pick a meal for each camper instead of defaulting to the same lunch for the whole group on Day 1.</p>
          </div>

          {/* 9 */}
          <div id="troubleshooting" className="sop-section scroll-mt-20 pt-10 pb-3 border-b-2 border-gray-200">
            <div className="flex items-center gap-3"><HelpCircle className="w-6 h-6 text-amber-700" /><h2 className="text-xl sm:text-2xl font-bold text-gray-900">9. Something Not Working?</h2></div>
          </div>
          <div className="mt-4 space-y-3">
            <div className="space-y-2">
              <div className="p-3 bg-gray-50 rounded-lg border"><strong className="text-sm">Group not found?</strong><p className="text-sm text-gray-600 mt-1">Try typing your group code differently: B1, B01, or your group name (Lobsters). The system accepts multiple formats.</p></div>
              <div className="p-3 bg-gray-50 rounded-lg border"><strong className="text-sm">Timer already at zero?</strong><p className="text-sm text-gray-600 mt-1">It's past 9:30 AM. Orders are locked. Only <strong>Brian Stein</strong> can reopen the window — find a supervisor with a radio and they'll call him.</p></div>
              <div className="p-3 bg-gray-50 rounded-lg border"><strong className="text-sm">Camper missing from the list?</strong><p className="text-sm text-gray-600 mt-1">First check the Attendance App — <strong>if the camper is marked absent today, they will not appear on the lunch ordering roster.</strong> If they're not absent, they probably aren't scheduled to be at camp today based on their session type (e.g. a Flex camper on a non-Flex day). Double-check the day's roster — if they really are at camp and still missing, tell your supervisor.</p></div>
              <div className="p-3 bg-gray-50 rounded-lg border"><strong className="text-sm">Save button not working?</strong><p className="text-sm text-gray-600 mt-1">Make sure you selected a lunch from the dropdown first. You can't save an empty selection. If still broken, try "Save All Orders" at the bottom.</p></div>
            </div>
          </div>

          <footer className="mt-12 pt-6 border-t text-center space-y-3 pb-8">
            <p className="text-sm font-bold text-amber-900">Tech Support: Brian Stein</p>
            <p className="text-xs text-gray-400">Rolling River Day Camp — Lunch App Tutorial — rrdcdining.com</p>
          </footer>
        </main>
      </div>

      {showBackToTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="fixed bottom-6 right-6 bg-amber-700 text-white p-3 rounded-full shadow-lg hover:bg-amber-600 z-50 print:hidden" data-testid="back-to-top"><ChevronUp className="w-5 h-5" /></button>
      )}
    </div>
  );
}
